import { Table } from 'antd';
import React from 'react';
import type { ReminderResponse } from './types/reminder';
import type { Student } from './types/student';

type ReminderStatusTableProps = {
  studentIds: string[];
  students: Student[];
  responses: Record<string, ReminderResponse>;
};

const statusLabels: Record<string, string> = {
  pending: 'Pending',
  soft_reminder_shown: 'Soft shown',
  snoozed: 'Snoozed',
  dismissed: 'Dismissed',
};

export default function ReminderStatusTable({
  studentIds,
  students,
  responses,
}: ReminderStatusTableProps) {
  const activeStudents = React.useMemo(
    () => students.filter((student) => student.active !== false),
    [students]
  );

  const studentMap = React.useMemo(() => {
    const m: Record<string, Student> = {};
    activeStudents.forEach((s) => {
      m[s.id] = s;
    });
    return m;
  }, [activeStudents]);

  const dataSource = React.useMemo(() => {
    return studentIds
      .filter((id) => Boolean(studentMap[id]))
      .map((id) => {
        const student = studentMap[id];
        const resp = responses[id];
        const showActionTime =
          resp?.status === 'snoozed' || resp?.status === 'dismissed';
        return {
          key: id,
          student: student?.name ?? id,
          status: resp ? statusLabels[resp.status] ??  (String(resp.status) === 'final_reminder_shown' ? 'pending' : resp.status) : 'Pending',
          actionTime:
            showActionTime && resp?.actionTime
              ? new Date(resp.actionTime).toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : '-',
        };
      });
  }, [studentIds, studentMap, responses]);

  const columns = [
    { title: 'Student', dataIndex: 'student', key: 'student' },
    { title: 'Status', dataIndex: 'status', key: 'status' },
    { title: 'Action Time', dataIndex: 'actionTime', key: 'actionTime' },
  ];

  return (
    <Table
      dataSource={dataSource}
      columns={columns}
      pagination={false}
      size="small"
    />
  );
}
